import { AfterViewInit, Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';

import { fuseAnimations } from '@fuse/animations';
import { Subject } from 'app/shared/models/subject';
import { AssessmentTemplate } from 'app/shared/models/assessment-template';
import { CheckListItem } from 'app/components/check-list/check-list.component';
import { AssessmentTemplateService } from 'app/shared/services/assessment-template.service';
import { take } from 'rxjs/operators';
import { Assessment, AssessmentAssessmentTypeLink } from 'app/shared/models/assessment';
import { UserService } from 'app/shared/services/user.service';
import { AuthService } from 'app/shared/services/auth.service';
import { SubjectAssessment } from 'app/shared/models/subject';
import { ReviewDialogComponent } from '../../referrals/review-dialog/review-dialog.component';
import { enumReviewStatus } from 'app/shared/models/review';
import { Utilities } from 'app/shared/helpers/utilities';
import { SubjectService } from 'app/shared/services/subject.service';

@Component({
  selector: 'assessment-dialog',
  templateUrl: './assessment-dialog.component.html',
  styleUrls: ['./assessment-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: fuseAnimations,
})
export class AssessmentDialogComponent implements OnInit, AfterViewInit {

  title: string;
  subject: Subject;
  assessment: Assessment;
  assessmentTemplate: AssessmentTemplate;
  assessmentTemplates: AssessmentTemplate[] = [];
  assessmentCategories: any[] = [];
  totalScore = 0;
  override = false;
  isAdmin = false;
  rejected = true;
  readOnly = false;
  hideDialog = false;
  form: UntypedFormGroup;

  constructor(
    public dialogRef: MatDialogRef<AssessmentDialogComponent>,
    private formBuilder: UntypedFormBuilder,
    private assessmentTemplateService: AssessmentTemplateService,
    private authService: AuthService,
    private userService: UserService,
    private subjectService: SubjectService,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) { }

  ngOnInit() {
    this.subject = this.data.subject;
    this.assessment = this.data.assessment;
    this.assessmentTemplate = this.data.assessmentTemplate || ((this.data.assessment) ? this.data.assessment.assessmentTemplate : undefined);
    if (this.data.readOnly){
      this.readOnly = this.data.readOnly;
      if (this.data.assessment.review){
        this.override = this.data.assessment.review.override;
      }
    }
    const subjectTypeName = (this.subject.isReferral && (this.subject.referral)) ? 'Referral' : this.subject.subjectType.name;
    this.title = `Assessment of ${subjectTypeName}: ${this.subject.summary}`; 
    
    this.form = this.formBuilder.group({
      assessmentTemplateId: [{value: -1, disabled: this.readOnly}],
    });

    this.assessmentTemplateService.getAllData(undefined, undefined, true).pipe(take(1)).subscribe((templates: AssessmentTemplate[]) => {
      if (templates){
        const validTemplates = templates.filter((i) => (i.assessmentTemplateAssessmentCategoryLinks && (i.assessmentTemplateAssessmentCategoryLinks.length > 0)));
        if (this.assessmentTemplate){
          const template = validTemplates.find((i) => i.id === this.assessmentTemplate.id);
          if (template){
            this.assessmentTemplate = template;
            this.assessmentTemplates.push(this.assessmentTemplate);
            this.form.controls['assessmentTemplateId'].patchValue(this.assessmentTemplate.id);
            this.setupCategories();
          }
          else{
            this.assessmentTemplates = validTemplates.filter((i) => !i.hideRecord).sort((a, b) => a.name.localeCompare(b.name));
          }
        }
        else{
          this.assessmentTemplates = validTemplates.filter((i) => !i.hideRecord).sort((a, b) => a.name.localeCompare(b.name));
        }
      }
    });

    this.isAdmin = this.authService.hasPriv('admin');
  }

  ngAfterViewInit(): void {
    Utilities.patchCheckboxAria();
  }

  assessmentTemplateChanged(ev: MatSelectChange) {
    const val = ev.value;
    this.assessmentTemplate = this.assessmentTemplates.find((i) => i.id === val);
    this.setupCategories();
  }

  setupCategories(){
    if (this.assessmentTemplate){
      this.totalScore = 0;
      const existingTypes = (this.assessment) ? this.assessment.assessmentAssessmentTypeLinks.map((i) => i.assessmentTypeId) : [];
      this.assessmentCategories = [];
      this.assessmentTemplate.assessmentTemplateAssessmentCategoryLinks.forEach((i) => {
        const category: any = i.assessmentCategory;
        category.score = 0;
        category.types = [];
        // convert to a set - this removes any duplicates automatically
        const types = new Set(i.assessmentCategory.assessmentTypes.filter((x) => !x.hideRecord));
        [...Array.from(types)].forEach((item) => 
          category.types.push(new CheckListItem(item.id, item.name, (existingTypes.indexOf(item.id) > -1), item.score)),
        );
        if (category.types && (category.types.length > 0)){
          category.types.filter((item) => item.checked).forEach((typ) => {
            category.score = category.score + typ.value;
          });
          this.assessmentCategories.push(category);
        }
        this.totalScore = this.totalScore + category.score;
      });
      this.rejected = (this.totalScore < this.assessmentTemplate.acceptanceThreshold);
    }
  }

  typeChanged(item, category){
    if (item && category){
      const assessmentCategory = this.assessmentCategories.find((i) => i.id === category.id);
      if (assessmentCategory){
          assessmentCategory.score = (item.checked) ? assessmentCategory.score + item.value : assessmentCategory.score - item.value;
          this.totalScore = (item.checked) ? this.totalScore + item.value : this.totalScore - item.value;
          this.rejected = (this.totalScore < this.assessmentTemplate.acceptanceThreshold);
      }
    }
  }

  accept(override?){
    const assessment = this.getAssessment(true);
    this.review(assessment, enumReviewStatus.Accepted, override);
  }

  reject(override?){
    const assessment = this.getAssessment(true);
    this.review(assessment, enumReviewStatus.Rejected, override);
  }

  saveProgress(){
    this.saveAndClose(false);
  }

  submit(){
    this.saveAndClose(true);
  }

  saveAndClose(completed: boolean){
    const assessment = this.getAssessment(completed);
    this.subject.subjectAssessments = this.subject.subjectAssessments || [];
    let subjectAssessment: SubjectAssessment;
    if (assessment.id){
      subjectAssessment = this.subject.subjectAssessments.find((i) => i.assessmentId === assessment.id);
      subjectAssessment.assessment = assessment;
    }
    if (!subjectAssessment)
    {
      this.subject.subjectAssessments.push(new SubjectAssessment({ assessment }));
    }

    this.subjectService.onAfterSave.pipe(take(1)).subscribe((subject) => {
      this.dialogRef.close({ data: { subject }});
    });
    this.subjectService.save(this.subject, 'Assessment successfully saved');
  }

  review(assessment, action, override){
    this.hideDialog = true;
    this.dialog.open(ReviewDialogComponent, {
        data: { 
          action,
          assessment, 
          override,
          subject: this.subject,
        }}).afterClosed().subscribe((result) => {
          if (result && result.data){
            this.dialogRef.close(result);
          }
          else{
            this.override = override;
            this.hideDialog = false;
          }
        });
  }

  viewReview(){
    if (this.assessment && this.assessment.review){
      this.dialogRef.close();
      this.dialog.open(ReviewDialogComponent, {
        data: { 
          review: this.assessment.review, 
          subject: this.subject,
        }});
    }
  }

  getAssessment(completed: boolean): Assessment{
    const assessment = new Assessment({
      id: ((this.assessment) ? (this.assessment.id || undefined) : undefined),
      assessmentTemplateId: this.assessmentTemplate.id,
      score: this.totalScore,
      acceptanceThreshold: this.assessmentTemplate.acceptanceThreshold,
      completed,
      completedById: (completed ? this.userService.getCurrentUserId() : undefined),
      completedDate: (completed ? new Date() : undefined),
      lastModifiedDate: new Date(),
    });
    const selectedTypeList = [];
    this.assessmentCategories.forEach((category) => {
      category.types.filter((i) => i.checked).forEach((typ) => {
        selectedTypeList.push(typ.id);
      });
    });
    const links = (this.assessment && this.assessment.assessmentAssessmentTypeLinks) ? this.assessment.assessmentAssessmentTypeLinks : [];
    // add existing links
    assessment.assessmentAssessmentTypeLinks = links.filter((x) => selectedTypeList.includes(x.assessmentTypeId));
    // add new links
    const existingLinks = assessment.assessmentAssessmentTypeLinks.map((x) => x.assessmentTypeId);
    selectedTypeList.forEach((x) => {
      if (!existingLinks.includes(x)){
        assessment.assessmentAssessmentTypeLinks.push(new AssessmentAssessmentTypeLink({
            assessmentTypeId: x,
          }));
      }
    });
    return assessment;
  }

  overrideChange(e){
    this.override = e.checked;
  }
}
